/**
 * useSearchesList — TanStack Query hook for the My Searches list.
 *
 * Keyed on `{owner, status, q, sort, order, page, page_size}` so any filter /
 * scope / search / sort / page change refetches. No polling: the underlying
 * data is a mirror refreshed by a cron worker, so nothing changes mid-session
 * in response to anything the user does here (unlike the imports list, which
 * polls because a parse or export lands on rows after the fact).
 *
 * The sort is a SERVER parameter (Trello #813): the API orders the whole
 * result set and pages it, so the rows a page carries are already in order
 * and the table renders them as given.
 */

import type {
  SearchStatusFilter,
  SearchesListResponse,
  SearchesListSortKey,
  SearchesListSortOrder,
} from "@nu-assist/contracts";
import { useQuery } from "@tanstack/react-query";

import type { ListSearchesParams, SearchesClient } from "../../../clients/searches.client";

/** The page's sort state: a column and a direction, or none (the default order). */
export type SearchesSort = { key: SearchesListSortKey; dir: SearchesListSortOrder } | null;

export interface UseSearchesListArgs {
  client: SearchesClient;
  /** `me` | `all` | a numeric Thrive user id — the canonical wire form. */
  owner: string;
  status: SearchStatusFilter;
  /** Free-text search; trimmed/empty is omitted. */
  q: string;
  /** `null` sends no `sort`/`order` at all — the API's newest-created order. */
  sort: SearchesSort;
  page: number;
  pageSize: number;
}

/** Build the wire params, omitting empties so the query key stays stable. */
export function toListParams(args: UseSearchesListArgs): ListSearchesParams {
  const params: ListSearchesParams = {
    owner: args.owner,
    status: args.status,
    page: args.page,
    page_size: args.pageSize,
  };
  const q = args.q.trim();
  if (q) params.q = q;
  if (args.sort) {
    params.sort = args.sort.key;
    params.order = args.sort.dir;
  }
  return params;
}

export function useSearchesList(args: UseSearchesListArgs) {
  const params = toListParams(args);
  return useQuery<SearchesListResponse>({
    queryKey: [
      "searches",
      "list",
      params.owner,
      params.status,
      params.q ?? "",
      params.sort ?? "",
      params.order ?? "",
      params.page,
      params.page_size,
    ],
    queryFn: () => args.client.listSearches(params),
    // Keep the previous page on screen while the next one loads (no flicker).
    placeholderData: (prev) => prev,
  });
}
